import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { MessageSquare, Rows2, ArrowLeftRight, Upload, X } from "lucide-react";
import { Document, DocumentVersion } from "@shared/schema";

const INDICATOR_COLORS = [
  "#ef4444",
  "#f97316",
  "#eab308",
  "#22c55e",
  "#3b82f6",
  "#a855f7",
];

interface ActionDrawerProps {
  document: Document;
  versions: DocumentVersion[];
  currentVersion: DocumentVersion | undefined;
  viewMode: "comment" | "browse" | "compare";
  onModeChange: (mode: "comment" | "browse" | "compare") => void;
  onVersionChange: (versionId: string) => void;
  onUploadNewVersion: () => void;
  updateDocumentColorMutation: any;
}

export function ActionDrawer({
  document,
  versions,
  currentVersion,
  viewMode,
  onModeChange,
  onVersionChange,
  onUploadNewVersion,
  updateDocumentColorMutation
}: ActionDrawerProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const controlsRef = useRef<HTMLDivElement>(null);
  const [measuredWidth, setMeasuredWidth] = useState(0);

  useEffect(() => {
    const saved = localStorage.getItem('actionDrawerExpanded');
    if (saved !== null) {
      setIsExpanded(JSON.parse(saved));
    }
  }, []);

  useEffect(() => {
    if (controlsRef.current) {
      setMeasuredWidth(controlsRef.current.scrollWidth);
    }
  });

  const toggleExpanded = () => {
    const newState = !isExpanded;
    setIsExpanded(newState);
    localStorage.setItem('actionDrawerExpanded', JSON.stringify(newState));
  };

  const controls = (
    <div ref={controlsRef} className="flex items-center gap-0.5">
      <div className="w-px h-3.5 bg-border/40 shrink-0 mx-1" />

      <Select
        value={document.defaultIndicatorColor}
        onValueChange={(color: string) => updateDocumentColorMutation.mutate(color)}
      >
        <SelectTrigger 
          className="w-7 h-7 p-0 border-0 rounded-sm bg-transparent hover:bg-accent/60 transition-colors duration-100 [&>svg]:hidden shrink-0 flex items-center justify-center focus:ring-0 focus:ring-offset-0"
          style={{ outlineColor: document.defaultIndicatorColor }}
        >
          <div 
            className="w-4 h-4 rounded-full"
            style={{ backgroundColor: document.defaultIndicatorColor }}
          />
        </SelectTrigger>
        <SelectContent className="w-auto p-2 bg-popover border border-border/60 rounded-md shadow-2xl backdrop-blur-xl" align="start">
          <div className="grid grid-cols-6 gap-1.5">
            {INDICATOR_COLORS.map((color) => (
              <button
                key={color}
                onClick={() => updateDocumentColorMutation.mutate(color)}
                className="w-6 h-6 p-0 rounded-full focus:outline-none transition-all duration-100 hover:scale-110"
              >
                <div 
                  className="w-4.5 h-4.5 rounded-full mx-auto"
                  style={{ 
                    backgroundColor: color,
                    width: '18px',
                    height: '18px',
                    boxShadow: document.defaultIndicatorColor === color 
                      ? `0 0 0 2px var(--background), 0 0 0 3px ${color}` 
                      : 'none'
                  }}
                />
              </button>
            ))}
          </div>
        </SelectContent>
      </Select>

      <div className="w-px h-3.5 bg-border/40 shrink-0 mx-1" />

      <ToggleGroup
        type="single"
        value={viewMode}
        onValueChange={(value) => value && onModeChange(value as "comment" | "browse" | "compare")}
        className="bg-transparent gap-0 rounded-none p-0"
      >
        {[
          { value: "comment", icon: MessageSquare, label: "Comment view" },
          { value: "browse", icon: Rows2, label: "Browse view" },
          { value: "compare", icon: ArrowLeftRight, label: "Compare view" },
        ].map(({ value, icon: Icon, label }) => (
          <ToggleGroupItem
            key={value}
            value={value}
            aria-label={label}
            className="relative px-2.5 h-7 flex items-center justify-center text-muted-foreground data-[state=on]:text-foreground data-[state=on]:bg-accent/60 rounded-sm border-0 transition-colors duration-100"
            style={viewMode === value ? { color: document.defaultIndicatorColor } : {}}
          >
            <Icon className="h-3.5 w-3.5" />
            {viewMode === value && (
              <motion.div
                layoutId="activeTab"
                className="absolute bottom-0 left-1.5 right-1.5 h-[1.5px] rounded-full"
                style={{ backgroundColor: document.defaultIndicatorColor }}
                transition={{ duration: 0.15, type: "spring", stiffness: 500, damping: 35 }}
              />
            )}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>

      {versions.length > 1 && viewMode !== "compare" && (
        <>
          <div className="w-px h-3.5 bg-border/40 shrink-0 mx-1" />
          <Select
            value={currentVersion?.id.toString()}
            onValueChange={(value) => {
              console.log("📍 ACTION DRAWER - Version selected:", value);
              onVersionChange(value);
            }}
          >
            <SelectTrigger 
              className="w-16 h-7 text-[10px] border-0 bg-transparent hover:bg-accent/60 rounded-sm px-2 transition-colors duration-100 focus:ring-0 focus:ring-offset-0 [&_span]:font-mono"
              style={{ outlineColor: document.defaultIndicatorColor, fontFamily: "'Space Mono', monospace" }}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-popover border-border/60 w-auto min-w-0 shadow-2xl backdrop-blur-xl">
              {versions.map((version: DocumentVersion) => (
                <SelectItem 
                  key={version.id} 
                  value={version.id.toString()}
                  disabled={version.hidden}
                  className={`text-[10px] ${version.hidden ? 'opacity-40' : ''}`}
                  style={{ fontFamily: "'Space Mono', monospace" }}
                >
                  v{version.version}{version.hidden ? ' (H)' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </>
      )}

      <div className="w-px h-3.5 bg-border/40 shrink-0 mx-1" />

      <button
        onClick={onUploadNewVersion}
        className="flex items-center justify-center px-2.5 h-7 text-muted-foreground hover:text-foreground hover:bg-accent/60 rounded-sm transition-colors duration-100"
        title="Upload new version"
      >
        <Upload className="h-3.5 w-3.5" />
      </button>
    </div>
  );

  return (
    <div className="w-full flex justify-center py-1.5">
      <div className="flex items-center bg-popover/90 border border-border/60 rounded-md h-10 backdrop-blur-xl shadow-lg">
        <button
          onClick={toggleExpanded}
          className="flex items-center justify-center px-3 h-full text-[10px] text-muted-foreground hover:text-foreground hover:bg-accent/60 rounded-l-md transition-colors duration-100 shrink-0"
          title={isExpanded ? "Collapse toolbar" : "Expand toolbar"}
          style={{ fontFamily: "'Space Mono', monospace" }}
        >
          {isExpanded ? (
            <X className="w-3.5 h-3.5" />
          ) : (
            <span>v{currentVersion?.version}</span>
          )}
        </button>

        <motion.div
          animate={{ 
            width: isExpanded ? measuredWidth || "auto" : 0,
            opacity: isExpanded ? 1 : 0
          }}
          initial={false}
          transition={{ duration: 0.15, ease: [0.4, 0, 0.2, 1] }}
          className="overflow-hidden"
        >
          {controls}
        </motion.div>
      </div>
    </div>
  );
}
